import { useContext, useState, useCallback, useRef, useEffect, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import { Search, Check, ChevronDown, X } from 'lucide-react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';
import { fuzzyFilter, normalizeForSearch } from '../../../utils/fuzzy';

// Fuzzy engine — single source of truth in `utils/fuzzy.js`. Re-exported here
// for backward compatibility (this field's unit tests import them from this
// module). The component body uses `fuzzyFilter` + `normalizeForSearch` above.
export { normalizeForSearch, levenshtein, fuzzyScore, fuzzyFilter } from '../../../utils/fuzzy';

/**
 * FuzzySelectField — a searchable combobox for reactive-json.
 *
 * A button trigger shows the current selection; opening it reveals a search
 * field (accent- and typo-tolerant fuzzy match) over a scrollable option list.
 * Writes the selected option's `value` (an ID) to `dataLocation` — so wiring it
 * to an entity-reference field never clears the reference. With
 * `allowFreeValue: true`, a typed value with no exact match can be committed
 * verbatim (e.g. a free-text country).
 *
 * ARIA combobox pattern (WCAG 1.2 / RGAA 11): trigger is a labelable <button
 * aria-haspopup="listbox">; the search input is role="combobox"
 * aria-controls/aria-activedescendant over a role="listbox" of role="option".
 *
 * Two-tier list (`restAggregation`): with BOTH a curated source and a searchUrl,
 * the panel shows, AT REST, the curated options first — in their source order —
 * then the values actually present in the data, until `restSize` is reached.
 * Those come from an Elasticsearch terms AGGREGATION, whose order must be
 * deterministic (`order: [{_count: desc}, {_key: asc}]`): ranking hits by
 * `_score` on an empty query only LOOKS stable — scores are all equal and ES
 * falls back to Lucene's internal doc order, which any merge can reshuffle.
 * Typing switches to the server search. Use it for an EMERGENT reference set
 * (services, job titles) where curation guides without hiding what exists; a
 * closed one (countries) needs no second tier.
 *
 * Three option sources (precedence: searchUrl > optionsUrl > dynamicOptions):
 *   - dynamicOptions : a preloaded store slot [{value,label}] (eager).
 *   - optionsUrl     : the widget fetches the whole collection on first open,
 *                      then fuzzy-filters client-side (medium sets).
 *   - searchUrl      : each debounced keystroke POSTs the query to an
 *                      Elasticsearch endpoint; the server filters + ranks
 *                      (large sets — companies, deals… — that can't be loaded
 *                      client-side). No client fuzzy; pass `valueLabel` so the
 *                      current value shows before any search.
 *
 * YAML usage (eager / lazy):
 *   - type: FuzzySelectField
 *     dataLocation: ~~.contact.data.salesTunnelStatus
 *     dynamicOptions: ~~.statusOptions        # [{value, label}]
 *     placeholder: "— Statut —"
 *     allowFreeValue: false                    # true for country/free text
 *     inputAttributes: { id: "c-status" }      # id target of an external <label for>
 *
 * YAML usage (two-tier — emergent reference set):
 *   - type: FuzzySelectField
 *     dataLocation: ~~.contact.data.department
 *     optionsUrl: "/data/departments-fr.json"   # tier 1, shown in FILE order
 *     searchUrl: "/api/elasticsearch/contacts/config/departments_suggest"
 *     searchMode: wildcard
 *     searchFields: ['department']
 *     optionValueKey: department                # keys on the ES hits…
 *     optionLabelKey: department                # …a curated {value,label} list
 *                                               #   still resolves (toOption falls back)
 *     restAggregation: values                   # tier 2, the ES terms agg
 *     restSize: 20                              # curated + fill, capped
 *     allowFreeValue: true
 *
 * YAML usage (server search — large reference set):
 *   - type: FuzzySelectField
 *     dataLocation: ~~.contact.data.companyId
 *     searchUrl: "/api/elasticsearch/companies/config/companies_suggest"
 *     searchFields: ['title']                  # ES fields matched full-text
 *     searchSize: 15
 *     optionValueKey: id                       # keys on each ES hit
 *     optionLabelKey: title
 *     valueLabel: ~~.contact.data.companyName  # shown before searching
 *     inputAttributes: { id: "c-company" }
 *
 * Multi-select (`multiple: true`) : la valeur est un TABLEAU. Résolution des
 * libellés de chips selon la source :
 *   - dynamicOptions : options préchargées → les chips se résolvent d'elles-mêmes.
 *   - optionsUrl : chargement paresseux (à la première ouverture) SAUF si le champ
 *     porte déjà une sélection — il faut alors la nommer, donc les options sont
 *     chargées au montage (ex. appartenance BU, filtres d'un widget enregistré).
 *   - searchUrl : les items déjà sélectionnés ne sont PAS dans les résultats de
 *     recherche. Deux façons de fournir leurs libellés :
 *       (a) exposer le champ back en EMBED (chaque item = objet {id/nid, title})
 *           → dataLocation reçoit un tableau d'OBJETS en lecture ; le widget en
 *           extrait valeur+libellé (via optionValueKey/LabelKey) et ÉCRIT toujours
 *           un tableau de scalaires. Ex. field_contacts d'un deal.
 *       (b) passer `selectedOptions` : un tableau [{value,label}] résolu par la
 *           page (source dédiée), quand dataLocation reste des scalaires.
 *     Dans les deux cas, les items choisis dans la session sont mémorisés pour
 *     rester lisibles après un enregistrement (le back ré-écho des scalaires).
 *
 * Create-on-the-fly (single mode): add `createSubmissionUrl` (+ optional
 * `createLabelField`, default "name") to let a typed, unmatched value be created
 * as a new entity via decoupled-save and immediately selected — e.g. create a
 * company straight from the contact form:
 *     createSubmissionUrl: "/decoupled-api/save?display=decoupled&entity_type=node&bundle=company"
 *     createLabelField: name          # the entity field that receives the text
 *
 * Create through the page's own FORM (single mode): when the creation needs more
 * than a name, `createOpenPath` replaces `createSubmissionUrl` — the "Créer « X »…"
 * row pre-fills the page's form and opens its modal instead of POSTing, and the
 * page selects the new entity (dataLocation + valueLabel) once its save succeeds:
 *     createOpenPath: ~~.showCompanyModal        # bool passé à true
 *     createLabelPath: ~~.newCompany.data.name   # reçoit le texte saisi
 *     createExtraPath: ~~.newCompany.data.website # reçoit la valeur dérivée (suggest)
 *
 * Suggestion from a related value (single mode): when the field is EMPTY,
 * `suggestFromValue` + `suggestSearchUrl` (+ `suggestFields`, `suggestMode`,
 * `suggestTransform`, `suggestLabel`) propose a one-click "probable" match —
 * e.g. infer the company from the contact's email domain:
 *     suggestFromValue: ~~.newContact.data.email
 *     suggestSearchUrl: "/api/elasticsearch/companies/config/companies_suggest"
 *     suggestFields: ['website']
 *     suggestMode: wildcard
 *     suggestTransform: emailDomain   # "john@acme.com" → "acme.com"
 *     suggestSkipValues: ['gmail.com', …]   # derived values to NOT suggest on
 *     suggestLabel: "Entreprise probable"
 *     createExtraField: website       # stamp the domain onto a newly-created company
 */
const FuzzySelectField = ({ props, path, datafield }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const inputAttributes = useEvaluatedAttributes(props.inputAttributes);

    const reactId = useId();
    const listboxId = `fsf-listbox-${reactId}`;
    const optionId = (i) => `fsf-opt-${reactId}-${i}`;

    const [open, setOpen] = useState(false);
    // Dépliage des puces — lecture seule, sans rapport avec l'ouverture du panneau.
    const [toutDeplie, setToutDeplie] = useState(false);
    // Retrait d'une puce en attente de confirmation ({value,label}), et mémoire
    // du « ne plus demander » — le temps de la page, pour CE champ seulement.
    const [retraitAConfirmer, setRetraitAConfirmer] = useState(null);
    const [retraitSansConfirmation, setRetraitSansConfirmation] = useState(false);
    const [nePlusDemander, setNePlusDemander] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    // Bumped on scroll/resize to recompute the (portalled) panel position.
    const [posTick, setPosTick] = useState(0);
    // Lazy mode (`optionsUrl`): options fetched on FIRST open, not at page mount.
    // null = not fetched yet; [] = fetched (possibly empty) or errored.
    const [lazyOptions, setLazyOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [optionsError, setOptionsError] = useState('');
    // Server-search mode (`searchUrl`): each (debounced) keystroke POSTs the
    // query to an Elasticsearch endpoint and the server returns + ranks the
    // matches. For large reference sets (companies, deals…) that can't be
    // loaded client-side.
    const [serverResults, setServerResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    // Distinct values of the field, from the ES terms aggregation (two-tier list).
    const [aggregationOptions, setAggregationOptions] = useState([]);
    const [searchError, setSearchError] = useState('');
    // Create-on-the-fly state. `localOption` ({value,label}) lets the trigger show
    // the new entity's name even though it isn't in the search results.
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');
    const [localOption, setLocalOption] = useState(null);
    // Multi + searchUrl : libellés des options choisies pendant cette session, pour
    // qu'un chip fraîchement ajouté soit lisible avant le rafraîchissement de la
    // source résolveuse (les résultats de recherche disparaissent au changement de
    // requête). Map { valeur(String) -> label }.
    const [localOptions, setLocalOptions] = useState({});
    // Suggestion ({value,label}) derived from `suggestFromValue`; dismissible.
    const [suggestion, setSuggestion] = useState(null);
    const [suggestDismissed, setSuggestDismissed] = useState(false);

    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const searchRef = useRef(null);
    const activeOptRef = useRef(null);
    const searchAbortRef = useRef(null);
    const searchTimerRef = useRef(null);
    const suggestAbortRef = useRef(null);
    const suggestTimerRef = useRef(null);

    // Options are normalised to {value, label}. `optionValueKey`/`optionLabelKey`
    // let the same widget consume differently-shaped sources without a per-source
    // processor: taxonomy → {value,label} (defaults), users → {id,name}, etc.
    // Evaluated on EVERY render (not memoised): the source is a template ref
    // string whose backing store fills in asynchronously — memoising on the ref
    // would freeze the initial empty list (globalDataContext keeps a stable
    // identity while its data mutates). Same approach as SelectField.
    const valueKey = props.optionValueKey || 'value';
    const labelKey = props.optionLabelKey || 'label';

    // Optional REACTIVE client-side filter for a dependent select: restrict the
    // eager (`dynamicOptions`) list to rows whose `filterByKey` equals the
    // resolved `filterByValue`. Re-evaluated on every render → changing the
    // controlling value (e.g. the deal's pipeline) refilters instantly, with no
    // refetch. Empty/absent filter value = no filtering (show all). Absent props
    // = zero impact on existing usages.
    const filterByKey = props.filterByKey || '';
    const filterByValue = evaluateTemplateValue({
        valueToEvaluate: props.filterByValue,
        globalDataContext,
        templateContext,
    });

    // Normalise any source row to {value, label}. Reads nested `.data` first
    // (raw decoupled collection item = {item_url, data:{…}}) then the row itself
    // (an already-flattened {id,name}/{value,label} from a dataProcessor).
    const toOption = (o) => {
        const src = (o && typeof o === 'object' && o.data && typeof o.data === 'object') ? o.data : o;
        // optionValueKey/LabelKey describe the SEARCH hits (e.g. `department` on
        // an ES row). A curated list feeding the same widget carries the canonical
        // {value,label} shape instead, so fall back to it rather than yielding an
        // undefined option that gets silently dropped from the list.
        const pick = (key, fallbackKey) => (src?.[key] !== undefined ? src[key] : src?.[fallbackKey]);
        return { value: pick(valueKey, 'value'), label: pick(labelKey, 'label') };
    };

    // Lazy source URL (resolved template ref). When set, options come from the
    // widget's own on-open fetch (lazyOptions) instead of a preloaded store slot.
    const optionsUrl = evaluateTemplateValue({
        valueToEvaluate: props.optionsUrl,
        globalDataContext,
        templateContext,
    }) || '';
    const searchUrl = evaluateTemplateValue({
        valueToEvaluate: props.searchUrl,
        globalDataContext,
        templateContext,
    }) || '';
    const searchFields = Array.isArray(props.searchFields) ? props.searchFields : ['title'];
    const searchSize = props.searchSize ?? 15;
    // AND by default: every typed word must match (precise typeahead). The
    // analyzed `match` folds accents, so "regie" finds "Régie". OR floods the
    // results with any-word matches and buries the intended record.
    const searchOperator = props.searchOperator === 'or' ? 'or' : 'and';
    // `match` (analyzed, accent-folding, relevance) vs `wildcard` (case-insensitive
    // *substring* over keyword fields — needed to match a FRAGMENT of a non-analyzed
    // field like job_title). Opt into wildcard with searchMode: wildcard.
    const searchMode = props.searchMode === 'wildcard' ? 'wildcard' : 'match';

    // Create-on-the-fly: when `createSubmissionUrl` is set and the typed text
    // matches no option, a "Créer « X »" row (top of the list) POSTs a new entity
    // to that decoupled-save URL and selects its returned id — so a company can be
    // created straight from the picker. `createLabelField` = the entity field that
    // receives the typed text (e.g. `name` → the company title).
    const createSubmissionUrl = evaluateTemplateValue({
        valueToEvaluate: props.createSubmissionUrl,
        globalDataContext,
        templateContext,
    }) || '';
    const createLabelField = props.createLabelField || 'name';
    // When creating, also copy a value derived from `suggestFromValue` into this
    // field (e.g. the email domain → the new company's `website`).
    const createExtraField = props.createExtraField || '';

    // Create through a FORM instead of a one-field POST: with `createOpenPath`,
    // the "Créer « X »" row writes the typed text at `createLabelPath` (and the
    // derived suggest value at `createExtraPath`, same role as `createExtraField`),
    // then flips the boolean at `createOpenPath` to true — the page opens its own
    // creation modal, pre-filled, and owns the save. The widget POSTs nothing in
    // that mode; the page selects the new entity by writing this field's
    // `dataLocation` (+ `valueLabel`) once its save succeeds.
    const storePath = (ref) => String(ref ?? '').replace(/^~~?\./, '');
    const createOpenPath = storePath(props.createOpenPath);
    const createLabelPath = storePath(props.createLabelPath);
    const createExtraPath = storePath(props.createExtraPath);

    // Suggestion from a related value: when the field is EMPTY, derive a query
    // from `suggestFromValue` (e.g. a contact email) and look up a matching
    // entity, proposed as a one-click "probable" association. `suggestTransform:
    // emailDomain` turns "john@acme.com" → "acme.com" (and skips free-mail
    // domains). The lookup POSTs to `suggestSearchUrl` (an ES endpoint) over
    // `suggestFields` in `suggestMode`.
    const suggestFromValue = evaluateTemplateValue({
        valueToEvaluate: props.suggestFromValue,
        globalDataContext,
        templateContext,
    });
    const suggestSearchUrl = evaluateTemplateValue({
        valueToEvaluate: props.suggestSearchUrl,
        globalDataContext,
        templateContext,
    }) || '';
    const suggestFields = Array.isArray(props.suggestFields) ? props.suggestFields : ['title'];
    const suggestMode = props.suggestMode === 'wildcard' ? 'wildcard' : 'match';
    // `suggestTransform` is a generic string transform (`emailDomain` = the part
    // after "@"). Any use-case-specific EXCLUSIONS (e.g. consumer free-mail
    // domains that never map to a company) are content: passed via
    // `suggestSkipValues`, so this generic widget carries no such knowledge.
    const suggestTransform = props.suggestTransform || 'none';
    const suggestLabel = props.suggestLabel || 'Suggestion';
    // Minimum derived-query length before a lookup fires — avoids weak matches on
    // a couple of characters. Default 2; the caller can raise it.
    const suggestMinLength = Number.isFinite(props.suggestMinLength) ? props.suggestMinLength : 2;
    const suggestSkip = new Set(
        Array.isArray(props.suggestSkipValues) ? props.suggestSkipValues.map((x) => String(x).toLowerCase()) : [],
    );
    const deriveSuggestQuery = (v) => {
        const s = String(v ?? '').trim().toLowerCase();
        if (!s) return '';
        let out = s;
        if (suggestTransform === 'emailDomain') {
            const at = s.lastIndexOf('@');
            const dom = at >= 0 ? s.slice(at + 1) : '';
            const lastDot = dom.lastIndexOf('.');
            // A REAL domain: has a TLD and a ≥3-char second-level label — filters
            // spurious substring matches on tiny/partial domains (e.g. "e.com").
            out = (lastDot > 0 && dom.slice(0, lastDot).length >= 3) ? dom : '';
        }
        return (out && out.length >= suggestMinLength && !suggestSkip.has(out)) ? out : '';
    };
    const suggestQuery = deriveSuggestQuery(suggestFromValue);

    // Two-tier list: name of the ES aggregation holding the distinct values, and
    // how many rows the resting list may hold in all (curated + fill).
    const restAggregation = typeof props.restAggregation === 'string' ? props.restAggregation : '';
    const restSize = Number.isFinite(props.restSize) ? props.restSize : 20;
    const typing = String(query ?? '').trim() !== '';

    // Option source precedence: server search > lazy fetch > eager store slot.
    let options;
    if (searchUrl && restAggregation && !typing) {
        // AT REST: curated first, in their own order, then the distinct values
        // observed in the data — deduplicated, capped. Both halves keep a stable
        // order, so the list never reshuffles between two openings.
        const curated = (lazyOptions ?? []).filter((o) => o != null);
        const seen = new Set(curated.map((o) => String(o.value)));
        const fill = aggregationOptions.filter((o) => !seen.has(String(o.value)));
        options = [...curated, ...fill].slice(0, restSize);
    } else if (searchUrl) {
        options = serverResults;
    } else if (optionsUrl) {
        options = lazyOptions ?? [];
    } else {
        // Eager mode: evaluated on EVERY render (see note above).
        let resolvedOptions = evaluateTemplateValue({
            valueToEvaluate: props.dynamicOptions ?? props.options,
            globalDataContext,
            templateContext,
        });
        // Dependent filter: keep only rows matching the controlling value.
        // Compared as strings (ids may be int vs string). Empty value = no filter.
        if (Array.isArray(resolvedOptions) && filterByKey && filterByValue != null && filterByValue !== '') {
            const fv = String(filterByValue);
            resolvedOptions = resolvedOptions.filter((o) => {
                const src = (o && typeof o === 'object' && o.data && typeof o.data === 'object') ? o.data : o;
                return String(src?.[filterByKey]) === fv;
            });
        }
        options = Array.isArray(resolvedOptions)
            ? resolvedOptions.filter((o) => o != null).map(toOption)
            : [];
    }

    const { formData, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: props.defaultFieldValue ?? '',
        globalDataContext,
        templateContext,
    });

    const allowFreeValue = props.allowFreeValue === true;
    // Multi-select : la valeur est un TABLEAU ; cliquer une ligne (case à cocher)
    // ajoute/retire sans fermer le panneau ; le trigger affiche des chips.
    const multiple = props.multiple === true;
    // Champ VERROUILLÉ (lecture seule). `disabled` sur le trigger ne suffit pas :
    // le « × » d'effacement est un bouton FRÈRE (interactif imbriqué interdit),
    // et l'ouverture du panneau se déclenche aussi au clavier — les trois voies
    // se ferment ici. Accepté sous `disabled:` comme sous `inputAttributes.disabled`,
    // la seconde forme étant celle qu'un YAML écrit déjà pour les attributs natifs.
    // `true` comme "true" : un attribut passé par un rjBuild peut arriver
    // stringifié. La chaîne "false" ne doit SURTOUT pas verrouiller le champ
    // (toute chaîne non vide est pourtant « vraie » en JS).
    const isOn = (v) => v === true || v === 'true';
    const disabled = isOn(props.disabled) || isOn(inputAttributes?.disabled);
    const placeholder = props.placeholder || '— Sélectionner —';
    const searchPlaceholder = props.searchPlaceholder || 'Rechercher';
    const emptyLabel = props.emptyLabel || 'Aucun résultat';

    const currentValue = formData ?? '';
    const currentValueScalar = multiple ? '' : currentValue;
    // En multi, la valeur courante est un TABLEAU dont chaque item est soit un
    // SCALAIRE (id/valeur), soit un OBJET de référence embarqué (ex. collection
    // decoupled {id/nid, title}). On extrait la valeur via optionValueKey/LabelKey
    // (toOption) → le widget gère les deux formes sans processor. L'écriture, elle,
    // reste toujours un tableau de scalaires (cf. commit).
    const selectedItems = multiple && Array.isArray(formData)
        ? formData.map((it) => (it && typeof it === 'object') ? toOption(it) : { value: it, label: undefined })
        : [];
    const selectedValues = selectedItems.map((it) => String(it.value));
    // Libellés portés directement par les items embarqués (forme objet).
    const selectedItemLabels = {};
    for (const it of selectedItems) {
        if (it.value != null && it.label != null) selectedItemLabels[String(it.value)] = it.label;
    }
    // Mémorise ces libellés : après un enregistrement, le back ré-écho des IDS
    // SCALAIRES (les objets embarqués disparaissent de formData) → sans ce cache,
    // les chips des items non modifiés retomberaient sur l'id brut jusqu'au reload.
    const selectedItemLabelsKey = JSON.stringify(selectedItemLabels);
    useEffect(() => {
        const entries = Object.entries(selectedItemLabels);
        if (!entries.length) return;
        setLocalOptions((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const [v, l] of entries) {
                if (next[v] !== l) { next[v] = l; changed = true; }
            }
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItemLabelsKey]);
    const isSelected = (value) => multiple
        ? selectedValues.includes(String(value))
        : String(value) === String(currentValueScalar);
    // In lazy mode the current value's label isn't known until options load;
    // the page can pass `valueLabel` (e.g. an ES-resolved name) to show it
    // immediately on the trigger without forcing a fetch.
    const valueLabel = evaluateTemplateValue({
        valueToEvaluate: props.valueLabel,
        globalDataContext,
        templateContext,
    });
    // Trigger label: matched option's label, else the provided valueLabel, else
    // the raw value (free value), else placeholder.
    const currentLabel = useMemo(() => {
        const hit = options.find((o) => String(o.value) === String(currentValue));
        if (hit) return hit.label;
        if (localOption && String(localOption.value) === String(currentValue)) return localOption.label;
        if (currentValue !== '' && valueLabel) return String(valueLabel);
        return currentValue !== '' ? String(currentValue) : '';
    }, [options, currentValue, valueLabel, localOption]);
    // Libellés des valeurs déjà sélectionnées en multi + searchUrl : les items
    // pré-sélectionnés ne sont PAS dans `options` (résultats de recherche). La page
    // les résout via `selectedOptions` (source ES/decoupled chargeant les entités
    // liées) ; `localOptions` couvre ceux choisis dans la session courante.
    const selectedOptionsRaw = evaluateTemplateValue({
        valueToEvaluate: props.selectedOptions,
        globalDataContext,
        templateContext,
    });
    const selectedOptionLabels = {};
    if (Array.isArray(selectedOptionsRaw)) {
        for (const raw of selectedOptionsRaw) {
            if (raw == null) continue;
            const o = toOption(raw);
            if (o.value != null) selectedOptionLabels[String(o.value)] = o.label;
        }
    }
    // Résolution du libellé d'un chip : options courantes → item choisi cette
    // session → libellé porté par l'item embarqué → sélection résolue par la page
    // (prop selectedOptions) → valeur brute (dernier recours).
    const chipLabelFor = (v) =>
        options.find((o) => String(o.value) === v)?.label
        ?? localOptions[v]
        ?? selectedItemLabels[v]
        ?? selectedOptionLabels[v]
        ?? v;

    // Chips affichées dans le trigger en mode multi.
    const selectedChips = multiple
        ? selectedValues.map((v) => ({ value: v, label: chipLabelFor(v) }))
        : [];

    // Repliement au-delà de `maxVisibleChips` : un contact peut appartenir à
    // des dizaines de segments, et la rangée de puces mange alors la fiche
    // entière. On en montre un nombre borné, suivi du reste en un compteur —
    // le panneau, lui, reste la façon de tout voir et de tout modifier.
    // 0 ou absent = aucun repliement (le comportement d'avant).
    const maxChips = Number(props.maxVisibleChips) || 0;
    const replieDisponible = maxChips > 0 && selectedChips.length > maxChips;
    const chipsRepliees = replieDisponible && !toutDeplie;
    const chipsAffichees = chipsRepliees ? selectedChips.slice(0, maxChips) : selectedChips;
    const chipsCachees = selectedChips.length - chipsAffichees.length;

    // Quand le repliement est en jeu, les puces sortent du bouton déclencheur :
    // « voir les autres » est un geste de LECTURE, pas d'édition, et il lui faut
    // son propre bouton — or un bouton dans un bouton est du HTML invalide et un
    // piège pour les technologies d'assistance. Les autres usages du widget
    // gardent leurs puces dans le déclencheur, comme avant.
    // Croix PAR PUCE : un retrait à la fois, au lieu d'une croix unique qui vide
    // tout. Là où elle est active, la croix globale disparaît — garder les deux
    // reviendrait à laisser en place le geste qu'on cherche à rendre impossible
    // par mégarde.
    const puceRetirable = multiple && !!props.chipRemove;

    // Les puces sortent du bouton déclencheur dès qu'elles portent un contrôle —
    // une croix de retrait, un compteur à déplier. Un bouton dans un bouton est
    // du HTML invalide et un piège pour les technologies d'assistance.
    //
    // Le déclencheur du repliement ne suffit PAS ici : en deçà du seuil, les
    // puces restaient dedans, donc sans croix, alors que la croix globale avait
    // déjà cédé la place — il ne restait plus aucun moyen de retirer un élément
    // sans aller le décocher dans le panneau.
    const pucesDehors = replieDisponible || puceRetirable;

    // Server-search results are already filtered + ranked by Elasticsearch — show
    // them verbatim. Otherwise apply the client-side fuzzy filter.
    const filtered = useMemo(
        () => (searchUrl ? options : fuzzyFilter(query, options, { limit: props.maxResults ?? 100 })),
        [searchUrl, query, options, props.maxResults],
    );

    // A free-value pseudo-entry appears when free text is allowed, something is
    // typed, and no option matches it exactly — letting the user commit as-is.
    const canCreate = (!!createSubmissionUrl || !!createOpenPath) && !multiple;
    const queryTyped = query.trim() !== '';
    const noExactMatch = !filtered.some((o) => normalizeForSearch(o.label) === normalizeForSearch(query));
    // Row 0 (top of the list) is either a "create" entry (when a createSubmissionUrl
    // is set) or a free-value entry (allowFreeValue) — never both.
    const showCreateOption = canCreate && queryTyped && noExactMatch;
    const showFreeOption = allowFreeValue && !showCreateOption && queryTyped && noExactMatch;
    const showRow0 = showCreateOption || showFreeOption;
    const rowCount = filtered.length + (showRow0 ? 1 : 0);

    const commit = useCallback((value) => {
        if (multiple) {
            // Toggle dans le tableau ; le panneau RESTE ouvert (multi-sélection).
            // On écrit TOUJOURS un tableau de scalaires (on extrait la valeur des
            // items embarqués éventuels), pour un POST propre côté back.
            const cur = Array.isArray(formData)
                ? formData.map((it) => (it && typeof it === 'object') ? toOption(it).value : it)
                : [];
            const exists = cur.some((v) => String(v) === String(value));
            const next = exists ? cur.filter((v) => String(v) !== String(value)) : [...cur, value];
            // À l'ajout, mémorise le libellé de l'option choisie (elle est dans les
            // résultats visibles) pour que son chip reste lisible même après que la
            // recherche ait changé (cf. localOptions).
            if (!exists) {
                const picked = options.find((o) => String(o.value) === String(value));
                if (picked && picked.label != null) {
                    setLocalOptions((prev) => ({ ...prev, [String(value)]: picked.label }));
                }
            }
            globalDataContext.updateData(next, formDataPath);
            searchRef.current?.focus();
            return;
        }
        globalDataContext.updateData(value, formDataPath);
        // Dependent reset: when this single-select commits a DIFFERENT value,
        // clear the field at `clearOnChange` (e.g. clear the stage when the
        // pipeline changes) so a now-incoherent selection can't be saved.
        if (props.clearOnChange && String(value) !== String(formData ?? '')) {
            // Resolve the target store path from the ref (global `~~.` / local `~.`).
            const clearPath = String(props.clearOnChange).replace(/^~~?\./, '');
            if (clearPath) globalDataContext.updateData('', clearPath);
        }
        setOpen(false);
        setQuery('');
        triggerRef.current?.focus();
    }, [multiple, formData, globalDataContext, formDataPath, options, props.clearOnChange, path, datafield]);

    // Create a new entity from the typed text: POST { data: { [createLabelField] } }
    // to the decoupled-save URL (through the app's patched fetch → credentials +
    // CSRF), then select its returned id. Remembers {value,label} so the trigger
    // shows the new name even though it isn't in the (search) options.
    const doCreate = useCallback(async (name) => {
        const trimmed = String(name ?? '').trim();
        if (!trimmed || creating) return;
        // Delegated mode: no POST here — pre-fill the page's creation form with
        // what was typed (and the derived suggest value), then open its modal.
        // The page selects the created entity when its own save succeeds.
        if (createOpenPath) {
            if (createLabelPath) globalDataContext.updateData(trimmed, createLabelPath);
            if (createExtraPath) globalDataContext.updateData(suggestQuery || '', createExtraPath);
            globalDataContext.updateData(true, createOpenPath);
            setOpen(false);
            setQuery('');
            triggerRef.current?.focus();
            return;
        }
        if (!createSubmissionUrl) return;
        setCreating(true);
        setCreateError('');
        try {
            const data = { [createLabelField]: trimmed };
            if (createExtraField && suggestQuery) data[createExtraField] = suggestQuery;
            const r = await fetch(createSubmissionUrl, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ submission_url: createSubmissionUrl, data }),
            });
            const result = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(result?.status?.message || `HTTP ${r.status}`);
            const newId = String(result?.id ?? result?.data?.id ?? '');
            if (!newId) throw new Error('Réponse de création invalide.');
            setLocalOption({ value: newId, label: trimmed });
            commit(newId);
        } catch (e) {
            setCreateError(e.message || 'Erreur de création');
        } finally {
            setCreating(false);
        }
    }, [createSubmissionUrl, createLabelField, createExtraField, suggestQuery, creating, commit,
        createOpenPath, createLabelPath, createExtraPath, globalDataContext]);

    // Accept the suggested entity: select it and remember its label.
    const acceptSuggestion = useCallback(() => {
        if (!suggestion) return;
        setLocalOption(suggestion);
        commit(suggestion.value);
        setSuggestion(null);
    }, [suggestion, commit]);

    // Lazy fetch: on first open, load options from `optionsUrl` through the app's
    // patched fetch (credentials + request cache/batcher apply). Fetched once.
    const fetchOptions = useCallback(async () => {
        if (!optionsUrl || lazyOptions !== null || optionsLoading) return;
        setOptionsLoading(true);
        setOptionsError('');
        try {
            const r = await fetch(optionsUrl, { credentials: 'include', headers: { Accept: 'application/json' } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const raw = await r.json();
            const arr = Array.isArray(raw) ? raw : [];
            setLazyOptions(
                arr.filter((o) => o != null).map(toOption)
                    .filter((o) => o.value != null && o.label != null && o.label !== ''),
            );
        } catch (e) {
            setOptionsError(e.message || 'Erreur de chargement');
            setLazyOptions([]);
        } finally {
            setOptionsLoading(false);
        }
    }, [optionsUrl, lazyOptions, optionsLoading, valueKey, labelKey]);

    // Server search: POST {search:{query,fields,operator}, pagination:{size}} to
    // the ES endpoint. Aborts the in-flight request so only the latest keystroke's
    // results land (rapid typing → last-wins). Uses the app's patched fetch, so
    // credentials + CSRF are added automatically (same as esFetch).
    const runServerSearch = useCallback(async (q) => {
        if (!searchUrl) return;
        searchAbortRef.current?.abort();
        const ac = new AbortController();
        searchAbortRef.current = ac;
        setSearchLoading(true);
        setSearchError('');
        try {
            const search = searchMode === 'wildcard'
                ? { query: q || '', fields: searchFields, mode: 'wildcard' }
                : { query: q || '', fields: searchFields, operator: searchOperator };
            const body = { search, pagination: { from: 0, size: searchSize } };
            const r = await fetch(searchUrl, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(body),
                signal: ac.signal,
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const data = await r.json();
            const contents = Array.isArray(data?.contents) ? data.contents : [];
            setServerResults(
                contents.filter((o) => o != null).map(toOption)
                    .filter((o) => o.value != null && o.label != null && o.label !== ''),
            );
            // Same response also carries the distinct values, already ordered by
            // the endpoint — the resting list reads them rather than re-querying.
            if (restAggregation) {
                const buckets = data?.aggregations?.[restAggregation]?.buckets;
                setAggregationOptions(
                    (Array.isArray(buckets) ? buckets : [])
                        .map((b) => ({ value: b?.key, label: b?.key }))
                        .filter((o) => o.value != null && o.value !== ''),
                );
            }
        } catch (e) {
            if (e.name === 'AbortError') return; // superseded by a newer keystroke
            setSearchError(e.message || 'Erreur de recherche');
            setServerResults([]);
        } finally {
            if (searchAbortRef.current === ac) setSearchLoading(false);
        }
    }, [searchUrl, searchFields, searchSize, searchOperator, searchMode, valueKey, labelKey, restAggregation]);

    // Charger les options AVANT toute ouverture quand il y a déjà une sélection
    // à nommer : sans ça le champ affiche l'ID brut (« 6 » au lieu d'« Eurelis »)
    // tant que l'utilisateur ne l'a pas ouvert — donc au rechargement d'un écran
    // comme juste après un enregistrement, sur les valeurs déjà en base. Le
    // paresseux garde tout son sens pour un champ VIDE (le cas courant), et
    // l'appel est mutualisé par le cache/batcher de requêtes : N champs sur la
    // même URL = une seule requête réseau.
    const hasSelectionToLabel = multiple ? selectedValues.length > 0 : String(currentValue ?? '') !== '';
    useEffect(() => {
        if (!optionsUrl || searchUrl || !hasSelectionToLabel) return;
        fetchOptions();
    }, [optionsUrl, searchUrl, hasSelectionToLabel, fetchOptions]);

    const openPanel = useCallback(() => {
        if (disabled) return;
        setActiveIndex(0);
        setOpen(true);
        fetchOptions();          // no-op unless lazy mode, not yet loaded
        if (searchUrl) runServerSearch(query); // seed initial (top) results
    }, [disabled, fetchOptions, searchUrl, runServerSearch, query]);

    // The panel is PORTALLED to <body> with fixed positioning so it is NEVER
    // clipped by an ancestor's overflow (e.g. a modal body). Compute its box from
    // the trigger, flipping above when there isn't room below (viewport bounds).
    const panelStyle = useMemo(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return { position: 'fixed', visibility: 'hidden' };
        const gap = 4;
        const spaceBelow = window.innerHeight - rect.bottom - gap;
        const spaceAbove = rect.top - gap;
        const up = spaceBelow < 220 && spaceAbove > spaceBelow;
        // Le portail est un frère dans <body> : son étagement est absolu, il doit
        // donc passer au-dessus de TOUTE surface qui peut contenir ce champ.
        // `--z-popover-over` est la couche du DS pour exactement ça — un menu
        // ancré, porté en portail, au-dessus de la surface qui l'a ouvert. Un
        // z-60 en dur suffisait tant que la surface était une modale au z-50 ;
        // il faisait disparaître la liste derrière une pop-in plein écran
        // (--z-modal-full) ou le chrome du builder.
        const box = { position: 'fixed', left: Math.round(rect.left), width: Math.round(rect.width), zIndex: 'var(--z-popover-over)' };
        return up
            ? { ...box, bottom: Math.round(window.innerHeight - rect.top + gap), maxHeight: Math.max(140, Math.min(340, spaceAbove)) }
            : { ...box, top: Math.round(rect.bottom + gap), maxHeight: Math.max(140, Math.min(340, spaceBelow)) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, posTick]);

    // While open, follow the trigger on scroll/resize (the portal is position:fixed).
    useEffect(() => {
        if (!open) return;
        const onMove = () => setPosTick((t) => t + 1);
        window.addEventListener('scroll', onMove, true); // capture inner scrolls too
        window.addEventListener('resize', onMove);
        return () => {
            window.removeEventListener('scroll', onMove, true);
            window.removeEventListener('resize', onMove);
        };
    }, [open]);

    // Focus the search field on open; keep the active option scrolled into view.
    useEffect(() => {
        if (open) searchRef.current?.focus();
    }, [open]);
    useEffect(() => {
        if (open) activeOptRef.current?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, open]);

    // Close on outside click / Escape.
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            // The panel is portalled outside rootRef, so check it too.
            const inTrigger = rootRef.current?.contains(e.target);
            const inPanel = panelRef.current?.contains(e.target);
            if (!inTrigger && !inPanel) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    // Cancel any pending debounce timer / in-flight search on unmount.
    useEffect(() => () => {
        clearTimeout(searchTimerRef.current);
        searchAbortRef.current?.abort();
        clearTimeout(suggestTimerRef.current);
        suggestAbortRef.current?.abort();
    }, []);

    // A new derived query (e.g. a different email domain) re-enables suggesting.
    useEffect(() => { setSuggestDismissed(false); }, [suggestQuery]);

    // Suggestion lookup: when the field is empty and a `suggestQuery` exists,
    // debounced-POST it to `suggestSearchUrl` and keep the top hit as a proposal.
    useEffect(() => {
        if (multiple || !suggestSearchUrl || currentValueScalar !== '' || !suggestQuery || suggestDismissed) {
            setSuggestion(null);
            return;
        }
        clearTimeout(suggestTimerRef.current);
        suggestTimerRef.current = setTimeout(async () => {
            suggestAbortRef.current?.abort();
            const ac = new AbortController();
            suggestAbortRef.current = ac;
            try {
                const search = suggestMode === 'wildcard'
                    ? { query: suggestQuery, fields: suggestFields, mode: 'wildcard' }
                    : { query: suggestQuery, fields: suggestFields, operator: 'and' };
                const r = await fetch(suggestSearchUrl, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ search, pagination: { from: 0, size: 1 } }),
                    signal: ac.signal,
                });
                if (!r.ok) return;
                const data = await r.json();
                const top = Array.isArray(data?.contents) ? data.contents[0] : null;
                const opt = top ? toOption(top) : null;
                setSuggestion(opt && opt.value != null && opt.label ? opt : null);
            } catch (e) {
                if (e.name !== 'AbortError') setSuggestion(null);
            }
        }, 400);
        return () => clearTimeout(suggestTimerRef.current);
        // toOption/suggestFields derive only from static YAML props (config-static),
        // so they are intentionally omitted from the deps — no stale-closure risk.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestQuery, suggestSearchUrl, currentValueScalar, multiple, suggestDismissed, suggestMode]);

    // Row layout: the free-value ("create") entry, when shown, is ALWAYS row 0
    // (top of the list); the filtered options follow, offset by one.
    const optBase = showRow0 ? 1 : 0;
    const chooseRow = useCallback((rowIndex) => {
        if (showRow0 && rowIndex === 0) {
            if (showCreateOption) doCreate(query.trim());
            else commit(query.trim());
            return;
        }
        const optIdx = rowIndex - (showRow0 ? 1 : 0);
        if (optIdx >= 0 && optIdx < filtered.length) {
            commit(filtered[optIdx].value);
        }
    }, [filtered, showRow0, showCreateOption, query, commit, doCreate]);

    const handleSearchKeyDown = useCallback((e) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((i) => (rowCount === 0 ? 0 : (i + 1) % rowCount));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((i) => (rowCount === 0 ? 0 : (i - 1 + rowCount) % rowCount));
                break;
            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setActiveIndex(Math.max(0, rowCount - 1));
                break;
            case 'Enter':
                e.preventDefault();
                if (rowCount > 0) chooseRow(activeIndex);
                break;
            case 'Escape':
                e.preventDefault();
                // Échap ferme CE panneau et rien d'autre : sans cela l'écouteur de
                // `window` (useEscapeClose) refermait aussi la pop-in hôte — on
                // perdait le formulaire « Nouvelle entreprise » en quittant le
                // sélecteur de filiale.
                e.stopPropagation();
                setOpen(false);
                setQuery('');
                triggerRef.current?.focus();
                break;
            case 'Tab':
                setOpen(false);
                break;
            default:
                break;
        }
    }, [rowCount, activeIndex, chooseRow]);

    const handleTriggerKeyDown = useCallback((e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPanel();
        }
    }, [openPanel]);

    /**
     * Retire UNE valeur de la sélection.
     *
     * Le champ portait jusqu'ici une seule croix, qui vidait tout : un clic de
     * travers effaçait vingt-deux segments d'un coup, et l'enregistrement
     * automatique gravait la perte avant qu'on ait compris. Chaque puce a
     * désormais la sienne, et `confirmChipRemove` ajoute une confirmation —
     * qu'on peut faire taire pour la suite de la session, sinon elle devient
     * le clic de trop qu'on fait sans lire.
     */
    const retirerValeur = useCallback((valeur) => {
        const restantes = (Array.isArray(formData) ? formData : [])
            .map((it) => (it && typeof it === 'object' ? toOption(it).value : it))
            .filter((v) => String(v) !== String(valeur));
        globalDataContext.updateData(restantes, formDataPath);
    }, [formData, globalDataContext, formDataPath, toOption]);

    const demanderRetrait = useCallback((chip) => {
        if (!props.confirmChipRemove || retraitSansConfirmation) {
            retirerValeur(chip.value);
            return;
        }
        setRetraitAConfirmer(chip);
    }, [props.confirmChipRemove, retraitSansConfirmation, retirerValeur]);

    const clearValue = useCallback((e) => {
        e.stopPropagation();
        if (multiple) {
            globalDataContext.updateData([], formDataPath);
            triggerRef.current?.focus();
            return;
        }
        commit('');
    }, [multiple, commit, globalDataContext, formDataPath]);

    // reactive-json normalises `class` → `className` on inputAttributes, so pull
    // BOTH out: whichever the caller wrote is the width/appearance override, and
    // neither may reach {...triggerRest} — spread after `className`, it would
    // replace the base row layout and drop the chevron/clear below the text.
    const { class: triggerClassRaw, className: triggerClassName, ...triggerRest } = inputAttributes || {};
    const triggerClass = [triggerClassRaw, triggerClassName].filter(Boolean).join(' ');
    const hasValue = multiple ? selectedChips.length > 0 : currentLabel !== '';

    return (
        <ActionDependant {...props}>
            <div className="relative" ref={rootRef}>
                {pucesDehors && hasValue && (
                    /* Rangée de LECTURE : voir ses segments ne doit pas ouvrir
                       l'éditeur. Le compteur et le repli sont de vrais boutons,
                       donc atteignables au clavier — ce qu'un <span> cliquable
                       n'aurait pas été, et ce qu'un bouton imbriqué dans le
                       déclencheur n'aurait pas eu le droit d'être. */
                    <div className="flex flex-wrap items-center gap-1 mb-1">
                        {chipsAffichees.map((c) => (
                            <span key={c.value} className="badge badge-primary badge-sm inline-flex items-center gap-1">
                                {c.label}
                                {puceRetirable && !disabled && (
                                    <button
                                        type="button"
                                        className="inline-flex items-center rounded hover:text-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iris-300"
                                        aria-label={`Retirer ${c.label}`}
                                        onClick={() => demanderRetrait(c)}
                                    >
                                        <X size={12} aria-hidden="true" />
                                    </button>
                                )}
                            </span>
                        ))}
                        {!replieDisponible ? null : chipsRepliees ? (
                            <button
                                type="button"
                                className="badge badge-neutral badge-sm cursor-pointer"
                                // Déplier FERME le panneau. Ces boutons vivent dans
                                // le conteneur du champ, donc le clic-au-dehors ne
                                // les voit pas comme un dehors : sans cela, un
                                // panneau déjà ouvert recouvrait la liste qu'on
                                // venait justement de demander à voir.
                                onClick={() => { setOpen(false); setToutDeplie(true); }}
                                aria-expanded="false"
                                title={`Afficher les ${chipsCachees} autres`}
                            >
                                +{chipsCachees}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="badge badge-neutral badge-sm cursor-pointer"
                                onClick={() => { setOpen(false); setToutDeplie(false); }}
                                aria-expanded="true"
                                title="Réduire la liste"
                            >
                                Replier
                            </button>
                        )}
                    </div>
                )}
                {/* Le déclencheur et ses boutons flottants ont leur PROPRE
                    conteneur positionné. Sans lui, la croix d'effacement
                    (`absolute inset-y-0`) s'étend sur toute la hauteur du champ,
                    rangée de puces comprise : cliquer « Replier » quand la rangée
                    est haute tombait sur la croix, et vidait la sélection —
                    qu'un enregistrement automatique s'empressait de graver. */}
                <div className="relative">
                {/* Trigger — a labelable <button> so an external <label for> works. */}
                <button
                    type="button"
                    ref={triggerRef}
                    // The base layout (input + flex row for text ↔ chevron/clear)
                    // always applies; a caller's `class` (e.g. a width) APPENDS and
                    // wins Tailwind conflicts — it must never replace the row layout,
                    // or the chevron/clear drop below the text.
                    className={`${multiple
                        ? 'input input-multi flex items-center justify-between gap-2 w-full text-left'
                        : 'input flex items-center justify-between gap-2 w-full text-left'}${triggerClass ? ' ' + triggerClass : ''}`}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    onClick={() => (open ? setOpen(false) : openPanel())}
                    onKeyDown={handleTriggerKeyDown}
                    {...triggerRest}
                    // Après le spread : `disabled` vient peut-être des attributs,
                    // et l'état verrouillé ne doit pas pouvoir être réactivé par
                    // un attribut écrit plus tôt.
                    disabled={disabled}
                    aria-disabled={disabled || undefined}
                >
                    {multiple ? (
                        hasValue && !pucesDehors ? (
                            <span className="flex flex-wrap items-center gap-1 min-w-0">
                                {selectedChips.map((c) => (
                                    <span key={c.value} className="badge badge-primary badge-sm">{c.label}</span>
                                ))}
                            </span>
                        ) : hasValue && pucesDehors ? (
                            // Les puces vivent au-dessus ; le déclencheur ne porte
                            // plus que l'invite d'ajout, pour rester un seul geste.
                            <span className="truncate text-slate-400">{props.addPlaceholder || 'Ajouter…'}</span>
                        ) : (
                            <span className="truncate text-slate-400">{placeholder}</span>
                        )
                    ) : (
                        <span className={hasValue ? 'truncate' : 'truncate text-slate-400'}>
                            {hasValue ? currentLabel : placeholder}
                        </span>
                    )}
                    <ChevronDown size={16} aria-hidden="true" className="flex-shrink-0 text-slate-400" />
                </button>
                {/* Clear = a real, keyboard-reachable <button> SIBLING of the trigger
                    (never nested inside it: interactive-in-interactive is invalid HTML
                    and an AT trap). Positioned just left of the chevron. */}
                {hasValue && !props.required && !disabled && !puceRetirable && (
                    <button
                        type="button"
                        aria-label="Effacer la sélection"
                        className="absolute right-8 inset-y-0 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iris-300"
                        onClick={clearValue}
                    >
                        <X size={14} aria-hidden="true" />
                    </button>
                )}
                </div>

                {/* Persistent live region: a region inserted already-populated is
                    not reliably voiced, so keep it in the DOM and toggle its text. */}
                <span className="sr-only" role="status" aria-live="polite">
                    {(!multiple && !hasValue && suggestion && !suggestDismissed) ? `${suggestLabel} : ${suggestion.label}` : ''}
                </span>
                {/* Suggestion (e.g. a company inferred from the email domain): a
                    one-click "probable" association, shown only while the field is
                    empty and closed. */}
                {!multiple && !hasValue && suggestion && !suggestDismissed && !open && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                        <span className="text-slate-500 flex-shrink-0" aria-hidden="true">{suggestLabel} :</span>
                        <button
                            type="button"
                            aria-label={`Sélectionner ${suggestion.label}`}
                            className="font-medium text-iris-600 hover:text-iris-700 hover:underline truncate rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iris-300"
                            onClick={acceptSuggestion}
                        >
                            {suggestion.label}
                        </button>
                        <button
                            type="button"
                            aria-label="Ignorer la suggestion"
                            className="flex-shrink-0 text-slate-400 hover:text-slate-600 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iris-300"
                            onClick={() => setSuggestDismissed(true)}
                        >
                            <X size={12} aria-hidden="true" />
                        </button>
                    </div>
                )}

                {open && createPortal(
                    <div
                        ref={panelRef}
                        style={panelStyle}
                        className="min-w-[14rem] bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden flex flex-col"
                    >
                        {/* Search field — the combobox input driving the listbox. */}
                        <div className="p-2 border-b border-slate-100 shrink-0">
                            <div className="relative">
                                <span className="absolute left-2.5 inset-y-0 flex items-center text-slate-400 pointer-events-none">
                                    <Search size={15} aria-hidden="true" />
                                </span>
                                <input
                                    ref={searchRef}
                                    type="text"
                                    className="input pl-8"
                                    role="combobox"
                                    aria-expanded="true"
                                    aria-controls={listboxId}
                                    aria-autocomplete="list"
                                    aria-activedescendant={rowCount > 0 ? optionId(activeIndex) : undefined}
                                    aria-busy={searchLoading || optionsLoading || creating}
                                    aria-label={searchPlaceholder}
                                    placeholder={searchPlaceholder}
                                    value={query}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setQuery(v);
                                        setActiveIndex(0);
                                        // Server mode: debounce the query → ES (250 ms).
                                        if (searchUrl) {
                                            clearTimeout(searchTimerRef.current);
                                            searchTimerRef.current = setTimeout(() => runServerSearch(v), 250);
                                        }
                                    }}
                                    onKeyDown={handleSearchKeyDown}
                                />
                            </div>
                        </div>

                        <ul
                            id={listboxId}
                            role="listbox"
                            aria-multiselectable={multiple || undefined}
                            className="flex-1 min-h-0 overflow-y-auto py-1"
                        >
                            {/* Free-value ("create") entry FIRST (row 0) when free text is
                                allowed and the query matches no option exactly — the new value
                                is proposed at the top of the list, not buried at the bottom. */}
                            {showRow0 && (
                                <li
                                    id={optionId(0)}
                                    role="option"
                                    aria-selected={false}
                                    aria-busy={creating || undefined}
                                    ref={activeIndex === 0 ? activeOptRef : null}
                                    className={
                                        'flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer border-b border-slate-100 ' +
                                        (activeIndex === 0 ? 'bg-iris-50 text-iris-700' : 'text-slate-500 hover:bg-slate-50')
                                    }
                                    onMouseEnter={() => setActiveIndex(0)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => (showCreateOption ? doCreate(query.trim()) : commit(query.trim()))}
                                >
                                    {showCreateOption ? (
                                        <span className="truncate">
                                            {creating ? 'Création…' : <>Créer «&nbsp;{query.trim()}&nbsp;»{createOpenPath ? '…' : null}</>}
                                        </span>
                                    ) : (
                                        <span className="truncate">Utiliser «&nbsp;{query.trim()}&nbsp;»</span>
                                    )}
                                </li>
                            )}

                            {filtered.map((opt, i) => {
                                const rowIndex = optBase + i;
                                const selected = isSelected(opt.value);
                                const active = rowIndex === activeIndex;
                                return (
                                    <li
                                        key={opt.value ?? i}
                                        id={optionId(rowIndex)}
                                        role="option"
                                        aria-selected={selected}
                                        ref={active ? activeOptRef : null}
                                        className={
                                            'flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ' +
                                            (active ? 'bg-iris-50 text-iris-700' : 'text-slate-700 hover:bg-slate-50')
                                        }
                                        onMouseEnter={() => setActiveIndex(rowIndex)}
                                        onMouseDown={(e) => e.preventDefault()} // keep input focus
                                        onClick={() => commit(opt.value)}
                                    >
                                        {multiple && (
                                            <span
                                                aria-hidden="true"
                                                className={
                                                    'flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ' +
                                                    (selected ? 'bg-iris-600 border-iris-600 text-white' : 'border-slate-300')
                                                }
                                            >
                                                {selected && <Check size={12} />}
                                            </span>
                                        )}
                                        <span className="truncate flex-grow">{opt.label}</span>
                                        {!multiple && selected && <Check size={15} aria-hidden="true" className="flex-shrink-0 text-iris-600" />}
                                    </li>
                                );
                            })}

                            {/* Status rows (not role="option": not selectable). Loading /
                                error take priority over the empty state. Covers both the
                                lazy fetch and the server-search fetch. */}
                            {(optionsLoading || searchLoading) && rowCount === 0 && (
                                <li aria-live="polite" className="px-3 py-2 text-sm text-slate-400">
                                    {searchLoading ? 'Recherche…' : 'Chargement…'}
                                </li>
                            )}
                            {(optionsError || searchError) && !optionsLoading && !searchLoading && (
                                <li aria-live="polite" className="px-3 py-2 text-sm text-rose-600">
                                    Erreur de chargement des options.
                                </li>
                            )}
                            {createError && (
                                <li aria-live="polite" className="px-3 py-2 text-sm text-rose-600">
                                    {createError}
                                </li>
                            )}
                            {!optionsLoading && !searchLoading && !optionsError && !searchError && rowCount === 0 && (
                                <li aria-live="polite" className="px-3 py-2 text-sm text-slate-400">
                                    {emptyLabel}
                                </li>
                            )}
                        </ul>
                    </div>,
                    document.body,
                )}
            </div>
                {retraitAConfirmer && createPortal(
                    /* Confirmation du retrait — la raison d'être de la croix par
                       puce : un clic de travers ne doit pas défaire une
                       appartenance sans qu'on l'ait voulu. Le libellé dit ce que
                       le geste NE fait pas, parce que c'est là qu'est le doute :
                       on retire la fiche de la liste, on ne supprime pas la liste. */
                    <div
                        className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/40 p-4"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Confirmer le retrait"
                        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setRetraitAConfirmer(null); } }}
                    >
                        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5">
                            <p className="font-semibold text-slate-900">Retirer « {retraitAConfirmer.label} » ?</p>
                            <p className="mt-1 text-sm text-slate-600">
                                Cette fiche sortira de la liste. La liste elle-même n'est pas supprimée, et
                                les autres fiches qu'elle contient ne changent pas.
                            </p>
                            <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                                <input
                                    type="checkbox"
                                    checked={nePlusDemander}
                                    onChange={(e) => setNePlusDemander(e.target.checked)}
                                />
                                Ne plus demander pour ce champ
                            </label>
                            <div className="mt-4 flex justify-end gap-2">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => { setNePlusDemander(false); setRetraitAConfirmer(null); }}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    autoFocus
                                    onClick={() => {
                                        if (nePlusDemander) setRetraitSansConfirmation(true);
                                        retirerValeur(retraitAConfirmer.value);
                                        setNePlusDemander(false);
                                        setRetraitAConfirmer(null);
                                    }}
                                >
                                    Retirer
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}

        </ActionDependant>
    );
};

export default FuzzySelectField;
